import { useCallback, useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { fmtDateTime } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

// Fix I: estado de la conexion con Resend y email de prueba.
const MAIL_STATUS = { sent: "enviado", failed: "no salió", skipped: "sin configurar" };

export default function EmailCard({ replyTo }) {
  const { toast } = useToast();
  const [st, setSt] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/academy/email/status").then(({ data }) => setSt(data)).catch(() => setSt(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/academy/email/test", { to: to.trim() || null });
      if (data.status === "sent") toast({ title: `Email de prueba enviado a ${data.to}`, description: "Revisá la bandeja de entrada y también el spam." });
      else toast({ title: "El email de prueba no salió", description: data.error || "Sin detalle.", variant: "destructive" });
      load();
    } catch (err) {
      toast({ title: "No se pudo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (!st) return null;
  const missing = [!st.key_configured && "RESEND_API_KEY", !st.from_address && "ACADEMY_EMAIL_FROM"].filter(Boolean);
  const ready = missing.length === 0;
  return (
    <div className="space-y-2" data-testid={ACADEMY.emailCard}>
      <p className={`text-sm ${ready ? "text-muted-foreground" : "text-[#8A6A2F]"}`} data-testid={ACADEMY.emailStatus}>
        {ready ? `Resend conectado. Los emails salen desde ${st.from_address}.` : `Faltan en el servidor: ${missing.join(" y ")}. Los carga el responsable de la plataforma; mientras tanto el link se manda por WhatsApp.`}
      </p>
      <p className={`text-sm ${st.public_base ? "text-muted-foreground" : "text-[#8A6A2F]"}`}>
        {st.public_base ? `Los links de los emails apuntan a ${st.public_base}.` : "Falta PUBLIC_APP_URL en el servidor: los emails con link no se envían."}
      </p>
      {!(replyTo || "").trim() && <p className="text-sm text-[#8A6A2F]">Falta «Responder a»: si el alumno contesta un email, la respuesta no llega a la academia.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Email de prueba (vacío: el tuyo)" data-testid={ACADEMY.emailTestInput} />
        <Button size="sm" variant="outline" onClick={sendTest} disabled={!ready || busy} data-testid={ACADEMY.emailTestButton}><Send className="w-4 h-4" /> {busy ? "Enviando..." : "Enviar email de prueba"}</Button>
      </div>
      {st.last.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-1" data-testid={ACADEMY.emailLast}>
          {st.last.map((m, i) => (
            <li key={i} className={m.status === "sent" ? "" : "text-[#A34B49]"}>
              {fmtDateTime(m.created_at)} · {m.template} a {m.to || "—"} · {MAIL_STATUS[m.status] || m.status}{m.status !== "sent" && m.error ? ` (${m.error})` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
